import { HorizontalStackedBarChart } from 'Components/BaseCharts/HorizontalStackedBarChart';
import { CSSColors, Colors } from 'Components/Colors';
import { CircleIndicator } from 'Components/Indicator/CircleIndicator';
import { PageVariant } from 'Components/Table/Table';
import { Text } from 'Components/Text/Text';
import { iso8601ToUsDateShort } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { effectivenessAsString, getEffectivenessVariantColor } from 'Models/OperationalControls';

import styles from './HeaderDashlets.module.css';
import { ControlAssessmentStatistics } from '../../Hooks/useControlAssessmentStatistics';

const robustColor = CSSColors.DARK_GREEN;
const strongColor = CSSColors.LIGHT_GREEN;
const moderateColor = CSSColors.YELLOW;
const weakColor = CSSColors.ORANGE;
const failColor = CSSColors.RED;
const unratedColor = CSSColors.DARK_GRAY;

export interface ControlsDashletProps {
    pageVariant: PageVariant;
    /** Calculated by `useControlAssessmentStatistics`. */
    statistics: ControlAssessmentStatistics;
    dueDate?: string;
}

/**
 * Renders a `HorizontalStackedBarChart` showing progress on the control assessments for a due diligence assessment.
 * Used in tables of all assessments as well as the header for a specific assessment.
 */
export const ControlsDashlet = (props: ControlsDashletProps) => {
    const textColor: Colors = props.pageVariant === 'standard' ? 'blue' : 'white';

    return (
        <>
            <HorizontalStackedBarChart
                name="Controls"
                bars={[
                    {
                        name: 'Fail',
                        value: props.statistics.breakdown.fail,
                        color: failColor,
                    },
                    {
                        name: 'Weak',
                        value: props.statistics.breakdown.weak,
                        color: weakColor,
                    },
                    {
                        name: 'Moderate',
                        value: props.statistics.breakdown.moderate,
                        color: moderateColor,
                    },
                    {
                        name: 'Strong',
                        value: props.statistics.breakdown.strong,
                        color: strongColor,
                    },
                    {
                        name: 'Robust',
                        value: props.statistics.breakdown.robust,
                        color: robustColor,
                    },
                    {
                        name: 'Unrated',
                        value: props.statistics.breakdown.unrated,
                        color: unratedColor,
                    },
                ]}
                onChartClick={() => void 0}
                chartDimensions={{ width: '100%', height: 50 }}
            />
            <div className={styles.horizontalBarChartTextDecoration}>
                <Text color={textColor} noStyles>
                    Average effectiveness: <CircleIndicator variant={getEffectivenessVariantColor(props.statistics.averageEffectiveness)} /> {effectivenessAsString(props.statistics.averageEffectiveness)}
                </Text>
                <div className={styles.rightAlignedColumn}>
                    <Text color={textColor} noStyles>
                        {props.statistics.assessed}/{props.statistics.total} assessed
                    </Text>
                    {props.dueDate && (
                        <Text color={textColor} noStyles>
                            Due {iso8601ToUsDateShort(props.dueDate)}
                        </Text>
                    )}
                </div>
            </div>
        </>
    );
};
